import { useSWRConfig } from "swr";
import { MAX_PER_UPDATE } from "./useUpdateMeetings";
import { useActingAsOverrideHeader } from "../auth";
import { useTokenRefreshHandler } from "../hooks";
import { useUserService } from "../services";
import { getUserToken } from "src/utils/jwtToken";
import { errorHandler } from "src/hooks/errorHandler";
import { Meeting } from "../types";

export type SetInstanceCoHostsBulkResult =
  | { meetingId: number; success: true }
  | { meetingId: number; success: false; error: unknown };

type setInstanceCoHostsBulkRequest = {
  id: number;
  ids: number[];
};

type setInstanceCoHostsBulkFailure = {
  meetingId: number;
  display: string;
};

type setInstanceCoHostsBulkResponse = {
  failures: setInstanceCoHostsBulkFailure[];
  instances: Meeting[];
};

export function useSetInstanceCoHostsBulk() {
  const accessToken = getUserToken();
  const { mutate, cache } = useSWRConfig();

  const service = useUserService();
  const tokenRefreshHandler = useTokenRefreshHandler();
  const override = useActingAsOverrideHeader();

  const headers: { [index: string]: string } = {
    "JWT-TOKEN": accessToken as string,
  };

  if (override) {
    headers.override = override;
  }

  return async (
    meetingInstanceIds: number[],
    coHostIds: number[],
  ): Promise<SetInstanceCoHostsBulkResult[]> => {
    const requests: setInstanceCoHostsBulkRequest[] = meetingInstanceIds.map(
      (meetingId) => ({
        id: meetingId,
        ids: coHostIds,
      }),
    );
    const batches: setInstanceCoHostsBulkRequest[][] = [];

    while (requests.length) {
      batches.push(requests.splice(0, MAX_PER_UPDATE));
    }

    const results: SetInstanceCoHostsBulkResult[] = [];

    for (let i = 0; i < batches.length; i += 1) {
      const batch = batches[i];

      // We intentionally limit co-host updates to the same batch size used
      // by other meeting bulk actions to avoid spiking the API.
      // eslint-disable-next-line no-await-in-loop
      const response: setInstanceCoHostsBulkResponse = await service
        .put(`/api/meetings/instance/cohosts`)
        .set(headers)
        .send(batch)
        .then(tokenRefreshHandler)
        .then((res: Response) => res.body)
        .then((res: setInstanceCoHostsBulkResponse) => {
          // @ts-ignore
          const keys = cache.keys();

          Array.from(keys)
            .filter((r: any) => r.startsWith("/api/meetings/instance"))
            .forEach((key: any) => {
              mutate(key);
            });

          return res;
        })
        .catch(errorHandler);

      const failuresByMeetingId = new Map(
        (response.failures ?? []).map((failure) => [
          failure.meetingId,
          failure,
        ]),
      );
      const successfulMeetingIds = new Set(
        (response.instances ?? []).map((instance) => instance.id),
      );

      batch.forEach(({ id }) => {
        const failure = failuresByMeetingId.get(id);
        if (failure) {
          results.push({
            meetingId: id,
            success: false,
            error: failure,
          });
          return;
        }

        if (successfulMeetingIds.has(id)) {
          results.push({ meetingId: id, success: true });
        }
      });
    }

    return results;
  };
}
